import usePanel from '@databases/panel/usePanel';
import { defaultStoreUtils } from '@databases/redux';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import { getService } from '@databases/service-container';
import { DatabaseChangeType } from '@databases/types/redux';
import { getValueId } from '@databases/utils/utils';
import { useEffect } from 'react';

import { waitForContentAPI } from '@canvas/frame-api/host-api/host-frame-context';

import { getThreadObjectFromId } from '../utils.ts';

import { useCommentActionsContext } from './useCommentContext.ts';
import { useDeleteComment } from './useDeleteComment.ts';

type CommentThread = {
  threadId: string;
  isResolved: boolean;
  commentCount: number;
  versionNumber: number;
};

type CommentThreads = Record<string, CommentThread[]>;

interface RegisterCommentHandlersProps {
  contentId: string;
  comments: CommentThreads;
  canEditContent: boolean;
  shouldOpenPanelForFocusedComment: boolean;
}

export const useRegisterCommentHandlers = ({
  contentId,
  comments,
  canEditContent,
  shouldOpenPanelForFocusedComment
}: RegisterCommentHandlersProps) => {
  const { showPanel } = usePanel();
  const { setOpenThread, setFocusedCommentThreadId } = useCommentActionsContext();
  const dispatch = defaultStoreUtils.useDispatch();
  const selectionsActions = getService('SelectionsActions');
  const { deleteComment } = useDeleteComment();

  /**
   * Register the comments panel select thread handler
   */
  useEffect(() => {
    const registerCommentsPanelSelectThread = async () => {
      const canvasFrameAPI = await waitForContentAPI();
      canvasFrameAPI?.confluenceAPI?.comments?.onCommentsPanelSelectThread?.(
        (threadId: string) => {
          setFocusedCommentThreadId(threadId);
          const commentThread = getThreadObjectFromId(threadId, comments);
          if (!commentThread?.entryId || !commentThread?.fieldId) {
            return;
          }

          if (shouldOpenPanelForFocusedComment) {
            showPanel('EntryViewPanel', {
              entryId: commentThread.entryId,
              isReadonly: !canEditContent
            });
          }
          selectionsActions.selectValues([
            {
              entryId: commentThread.entryId,
              fieldId: commentThread.fieldId
            }
          ]);
        }
      );
    };

    registerCommentsPanelSelectThread();

    return () => {
      waitForContentAPI().then((canvasFrameAPI) =>
        canvasFrameAPI?.confluenceAPI?.comments?.onCommentsPanelSelectThread?.(() => {})
      );
    };
  }, [
    comments,
    selectionsActions,
    canEditContent,
    shouldOpenPanelForFocusedComment,
    showPanel,
    setFocusedCommentThreadId,
    contentId
  ]);

  /**
   * Register the comment reopen handler
   */
  useEffect(() => {
    const attachCommentReopen = async () => {
      const canvasFrameAPI = await waitForContentAPI();
      canvasFrameAPI?.confluenceAPI?.comments?.registerOnReopenResolvedComment?.((e) => {
        const commentThread = getThreadObjectFromId(e.referenceId, comments);
        if (
          commentThread?.fieldId &&
          commentThread.entryId &&
          commentThread.thread &&
          e.referenceId
        ) {
          dispatch(
            addChangeToDraft(contentId, {
              type: DatabaseChangeType.SET_VALUE_COMMENT_THREAD_RESOLVED,
              entryId: commentThread.entryId,
              fieldId: commentThread.fieldId,
              threadId: e.referenceId,
              isResolved: false
            })
          );
          setOpenThread({
            valueId: getValueId(commentThread.entryId, commentThread.fieldId),
            threadId: commentThread.thread.threadId
          });
        }
      });
    };

    attachCommentReopen();

    return () => {
      waitForContentAPI().then((canvasFrameAPI) =>
        canvasFrameAPI?.confluenceAPI?.comments?.registerOnReopenResolvedComment?.(
          () => {}
        )
      );
    };
  }, [comments, contentId, dispatch, setOpenThread]);

  /**
   * Register the comment delete handler
   */
  useEffect(() => {
    const attachCommentDelete = async () => {
      const canvasFrameAPI = await waitForContentAPI();
      canvasFrameAPI?.confluenceAPI?.comments?.registerOnDeleteComment?.((threadId) => {
        const commentThread = getThreadObjectFromId(threadId, comments);
        if (
          !commentThread?.entryId ||
          !commentThread?.fieldId ||
          !commentThread?.thread
        ) {
          return;
        }

        deleteComment({
          contentId,
          entryId: commentThread.entryId,
          fieldId: commentThread.fieldId,
          threadId: commentThread.thread.threadId,
          externalId: threadId,
          commentCount: commentThread.thread.commentCount,
          handleClose: () => {
            setOpenThread({ valueId: undefined });
            setFocusedCommentThreadId(undefined);
            selectionsActions.deselectAll();
          }
        });
      });
    };

    attachCommentDelete();

    return () => {
      waitForContentAPI().then((canvasFrameAPI) =>
        canvasFrameAPI?.confluenceAPI?.comments?.registerOnDeleteComment?.(() => {})
      );
    };
  }, [
    comments,
    contentId,
    deleteComment,
    selectionsActions,
    setFocusedCommentThreadId,
    setOpenThread
  ]);

  /*
   * Register the comment resolve handler
   */
  useEffect(() => {
    const attachCommentResolve = async () => {
      const canvasFrameAPI = await waitForContentAPI();
      canvasFrameAPI?.confluenceAPI?.comments?.registerOnResolveComment?.((threadId) => {
        const commentThread = getThreadObjectFromId(threadId, comments);
        if (commentThread?.entryId && commentThread?.fieldId) {
          dispatch(
            addChangeToDraft(contentId, {
              type: DatabaseChangeType.SET_VALUE_COMMENT_THREAD_RESOLVED,
              entryId: commentThread.entryId,
              fieldId: commentThread.fieldId,
              threadId,
              isResolved: true
            })
          );
          setOpenThread({ valueId: undefined });
          setFocusedCommentThreadId(undefined);
        }
      });
    };

    attachCommentResolve();

    return () => {
      waitForContentAPI().then((canvasFrameAPI) =>
        canvasFrameAPI?.confluenceAPI?.comments?.registerOnResolveComment?.(() => {})
      );
    };
  }, [comments, contentId, dispatch, setFocusedCommentThreadId, setOpenThread]);

  /**
   * Register the comment update handler
   */
  useEffect(() => {
    const attachCommentUpdate = async () => {
      const canvasFrameAPI = await waitForContentAPI();
      canvasFrameAPI?.confluenceAPI?.comments?.registerOnUpdateComment?.(
        (threadId: string, action?: 'reply' | 'edit') => {
          // Only increment comment count when a reply is added
          if (action !== 'reply') {
            return;
          }

          const commentThread = getThreadObjectFromId(threadId, comments);
          if (commentThread?.entryId && commentThread?.fieldId) {
            dispatch(
              addChangeToDraft(contentId, {
                type: DatabaseChangeType.INCREMENT_VALUE_COMMENT_THREAD_COMMENT_COUNT,
                entryId: commentThread.entryId,
                fieldId: commentThread.fieldId,
                threadId
              })
            );
          }
        }
      );
    };

    attachCommentUpdate();

    return () => {
      waitForContentAPI().then((canvasFrameAPI) =>
        canvasFrameAPI?.confluenceAPI?.comments?.registerOnUpdateComment?.(() => {})
      );
    };
  }, [comments, contentId, dispatch]);
};
